import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useRouterState } from '@tanstack/react-router';
import { ChevronDown, LogOut } from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
  cn,
  remember,
  remembered,
} from '@noissue/os-ui';
import { meQueryOptions } from '../api';
import { signOut } from '../auth-client';
import type { ModuleNavItem, RegisteredModule } from '../types';
import { Icon } from './icon';
import { Logo } from './logo';
import { RouteBoundary } from './route-boundary';

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .map((part) => part[0])
    .filter(Boolean)
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

/**
 * The shell chrome: a single left sidebar — logo at the top, the RBAC-filtered
 * module nav in the middle (server decides visibility via /api/me), and the user
 * menu pinned to the bottom. No top bar; the route mounts in a per-route boundary
 * filling the rest.
 */
export function AppLayout({ modules }: { modules: RegisteredModule[] }) {
  const { data: me } = useQuery(meQueryOptions);
  const routeId = useRouterState({ select: (s) => s.location.pathname });

  const visible = useMemo(
    () => (me ? modules.filter((m) => me.visibleModuleIds.includes(m.id)) : []),
    [me, modules],
  );

  /*
   * Collapse the visible modules into sidebar sections. Modules sharing a heading merge
   * into one section — see `RegisteredModule.section`. Order is first-declaration order in
   * the registry, and a module's own items stay contiguous within its section.
   *
   * Matching is exact: "Company" and "company" are two sections. Headings are a handful of
   * literals in one file, and an exact match shows a typo rather than quietly absorbing it.
   */
  const sections = useMemo(() => {
    const out: { heading: string; navItems: ModuleNavItem[] }[] = [];
    const granted = (item: ModuleNavItem) =>
      (item.requiredPermissions ?? []).every((p) => me?.permissions.includes(p));
    for (const m of visible) {
      const heading = m.section ?? m.name;
      const items = m.navItems.filter(granted);
      const existing = out.find((s) => s.heading === heading);
      if (existing) existing.navItems.push(...items);
      else out.push({ heading, navItems: items });
    }
    return out;
  }, [visible, me]);

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background text-foreground">
      <aside className="flex w-60 shrink-0 flex-col border-r bg-card">
        {/* 71 px + the 1 px rule below = 72, the height every page header is drawn at. */}
        <div className="flex h-[71px] shrink-0 items-center px-4">
          <Logo className="h-5 w-auto text-primary" />
          <span className="ml-[2px] text-[12px] font-semibold tracking-tight text-[#2C1847]">
            OS
          </span>
        </div>
        <Separator />
        {/*
          Stable gutter: a scrollbar appearing (a group opened) mustn't shift the chevrons.
          Real right padding as well, not the gutter standing in for it: with overlay scrollbars
          (macOS default) the gutter is 0px, with classic ones (a mouse attached, or "Show scroll
          bars: Always") it is the scrollbar itself — either way the chevrons need 16px clear of
          it. `pr-3` here + the heading's `pr-1` = 16.
        */}
        <nav className="mt-4 flex-1 overflow-y-auto py-2 pr-3 pl-2 [scrollbar-gutter:stable] [scrollbar-width:thin]">
          {sections.map((section) => (
            <NavSection
              key={section.heading}
              heading={section.heading}
              items={section.navItems}
              // Folding the only group away would leave an empty sidebar: nothing to fold it for.
              collapsible={sections.length > 1}
            />
          ))}
        </nav>

        {me && (
          <>
            <Separator />
            <div className="p-2">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-accent hover:text-accent-foreground focus-visible:outline-none focus-visible:ring-[1px] focus-visible:ring-ring/50"
                  >
                    <Avatar className="size-8">
                      <AvatarFallback className="text-xs">
                        {initialsOf(me.user.name)}
                      </AvatarFallback>
                    </Avatar>
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-medium">{me.user.name}</span>
                      <span className="block truncate text-xs text-muted-foreground">
                        {me.user.email}
                      </span>
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="top" align="start" className="w-[14rem]">
                  <DropdownMenuLabel className="truncate">{me.user.email}</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    onClick={() => signOut().then(() => (window.location.href = '/login'))}
                  >
                    <LogOut className="size-4" /> Sign out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        )}
      </aside>

      {/*
        The content area. Framing — the 80rem cap and who owns the scroll — is applied
        PER ROUTE in apps/web/src/router.tsx from each route's declared `layout`, not
        here: a `full` page (the quarterly planner) needs the whole width and its own
        two-axis scrolling, which a shared scrolling column cannot give it.

        So this stays `overflow-hidden` and hands its full height down. Every route
        wrapper is `h-full` and decides for itself whether to scroll.
      */}
      <main className="min-w-0 flex-1 overflow-hidden">
        <RouteBoundary routeKey={routeId}>
          <Outlet />
        </RouteBoundary>
      </main>
    </div>
  );
}

/** Where each heading's open/closed state is remembered, per viewer. */
const NAV_SCOPE = 'os-nav';

const HEADING =
  'flex h-9 w-full items-center justify-between rounded-md pl-3 pr-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground/70';

/**
 * One sidebar heading and its items. When `collapsible`, the heading folds the items away; each
 * heading remembers its own state in this browser, and every heading starts open. A plain heading
 * ignores any state remembered from when there were more groups.
 */
function NavSection({
  heading,
  items,
  collapsible,
}: {
  heading: string;
  items: ModuleNavItem[];
  collapsible: boolean;
}) {
  const [open, setOpen] = useState(
    () => remembered(NAV_SCOPE, heading, ['open', 'closed'] as const, 'open') === 'open',
  );

  if (!collapsible)
    return (
      <div>
        <div className={HEADING}>{heading}</div>
        <NavItems items={items} />
      </div>
    );

  return (
    <Collapsible
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        remember(NAV_SCOPE, heading, next ? 'open' : 'closed');
      }}
    >
      <CollapsibleTrigger
        className={cn(
          HEADING,
          'transition-colors hover:text-muted-foreground focus-visible:outline-none focus-visible:ring-[1px] focus-visible:ring-ring/50',
        )}
      >
        {heading}
        <ChevronDown
          className={cn(
            'size-3.5 transition-transform motion-reduce:transition-none',
            !open && 'rotate-180',
          )}
          aria-hidden
        />
      </CollapsibleTrigger>
      <CollapsibleContent className="overflow-hidden data-[state=closed]:animate-collapsible-up data-[state=open]:animate-collapsible-down motion-reduce:animate-none">
        <NavItems items={items} />
      </CollapsibleContent>
    </Collapsible>
  );
}

function NavItems({ items }: { items: ModuleNavItem[] }) {
  return (
    // The gap to the next heading lives on this wrapper, inside the folding box, so it folds
    // away with the items (a closed group adds none) instead of jumping at the end.
    <div className="mb-4 mt-1 space-y-1">
      {items.map((item) => (
        <Link
          key={item.path}
          to={item.path}
          activeOptions={{ exact: true }}
          className={cn(
            'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground',
            // Inset: the folding content clips anything drawn outside the item.
            'focus-visible:outline-none focus-visible:ring-[1px] focus-visible:ring-inset focus-visible:ring-ring/50',
            '[&.active]:bg-accent [&.active]:text-accent-foreground',
          )}
        >
          <Icon name={item.icon} className="size-4" />
          {item.label}
        </Link>
      ))}
    </div>
  );
}
